class StoreLocator {
    constructor(options) {
        this.sectionId = options.sectionId;
        this.csvUrl = options.csvUrl;
        this.storesPerPage = options.storesPerPage || 12;
        this.enableMap = options.enableMap || false;
        this.stores = [];
        this.filteredStores = [];
        this.currentPage = 1;

        this.searchInput = document.getElementById(`store-search-${this.sectionId}`);
        this.filterSelect = document.getElementById(`store-filter-${this.sectionId}`);
        this.loadingEl = document.getElementById(`store-loading-${this.sectionId}`);
        this.gridEl = document.getElementById(`store-grid-${this.sectionId}`);
        this.emptyEl = document.getElementById(`store-empty-${this.sectionId}`);
        this.paginationEl = document.getElementById(`store-pagination-${this.sectionId}`);

        this.init();
    }

    async init() {
        console.log('Initializing StoreLocator with sectionId:', this.sectionId);
        console.log('Elements found:', {
            searchInput: !!this.searchInput,
            filterSelect: !!this.filterSelect,
            loadingEl: !!this.loadingEl,
            gridEl: !!this.gridEl,
            emptyEl: !!this.emptyEl,
            paginationEl: !!this.paginationEl
        });

        if (!this.gridEl) {
            console.error('Grid element not found!');
            return;
        }

        this.showLoading();
        await this.loadStores();
        this.populateStateFilter();
        this.renderStores();
        this.hideLoading();
        this.attachEventListeners();
    }

    async loadStores() {
        try {
            console.log('Loading stores from:', this.csvUrl);
            const response = await fetch(this.csvUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const csvText = await response.text();
            console.log('CSV loaded, length:', csvText.length);

            this.stores = this.parseCSV(csvText);
            console.log('Parsed stores:', this.stores.length);

            this.filteredStores = [...this.stores];
        } catch (error) {
            console.error('Error loading stores:', error);
            this.stores = [];
            this.filteredStores = [];

            // Show error message to user
            if (this.gridEl) {
                this.gridEl.innerHTML = `<div class="store-locator__empty"><p>Error loading stores. Please check the console for details.</p></div>`;
                this.emptyEl.classList.remove('hidden');
            }
        }
    }

    parseCSV(csvText) {
        // Normalize line endings (handle both \r\n and \n)
        const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalizedText.trim().split('\n');
        console.log('CSV lines:', lines.length);

        if (lines.length < 2) {
            console.warn('CSV has less than 2 lines');
            return [];
        }

        const headers = this.parseCSVLine(lines[0]);
        console.log('CSV headers:', headers.length, headers);
        const stores = [];

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue; // Skip empty lines

            const values = this.parseCSVLine(line);

            // Allow some flexibility in column count (some rows might have trailing commas)
            if (values.length < headers.length) {
                // Pad with empty strings if we have fewer values
                while (values.length < headers.length) {
                    values.push('');
                }
            } else if (values.length > headers.length) {
                // Trim excess values
                values = values.slice(0, headers.length);
            }

            const store = {};
            headers.forEach((header, index) => {
                store[header] = (values[index] || '').trim();
            });

            // Only include published stores with a name
            if (store.status === 'publish' && store.name) {
                stores.push(store);
            } else if (store.name && store.status !== 'publish') {
                console.log(`Skipping store "${store.name}" - status: ${store.status}`);
            }
        }

        console.log(`Successfully parsed ${stores.length} stores`);
        if (stores.length > 0) {
            console.log('First store example:', stores[0]);
        }
        return stores;
    }

    parseCSVLine(line) {
        const values = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        values.push(current.trim());
        return values;
    }

    populateStateFilter() {
        const states = new Set();
        this.stores.forEach(store => {
            if (store.state) {
                states.add(store.state);
            }
        });

        const sortedStates = Array.from(states).sort();
        sortedStates.forEach(state => {
            const option = document.createElement('option');
            option.value = state;
            option.textContent = state;
            this.filterSelect.appendChild(option);
        });
    }

    attachEventListeners() {
        if (this.searchInput) {
            this.searchInput.addEventListener('input', () => this.handleSearch());
        }

        if (this.filterSelect) {
            this.filterSelect.addEventListener('change', () => this.handleFilter());
        }
    }

    handleSearch() {
        this.currentPage = 1;
        this.filterStores();
    }

    handleFilter() {
        this.currentPage = 1;
        this.filterStores();
        // Scroll to top of results
        if (this.gridEl) {
            this.gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    filterStores() {
        const searchTerm = this.searchInput.value.toLowerCase().trim();
        const selectedState = this.filterSelect.value;

        this.filteredStores = this.stores.filter(store => {
            const matchesSearch = !searchTerm ||
                store.name.toLowerCase().includes(searchTerm) ||
                store.city.toLowerCase().includes(searchTerm) ||
                store.state.toLowerCase().includes(searchTerm) ||
                (store.address && store.address.toLowerCase().includes(searchTerm));

            const matchesState = !selectedState || store.state === selectedState;

            return matchesSearch && matchesState;
        });

        this.renderStores();
    }

    renderStores() {
        if (!this.gridEl) {
            console.error('Grid element not found in renderStores');
            return;
        }

        this.gridEl.innerHTML = '';
        console.log('Rendering stores. Filtered count:', this.filteredStores.length);

        if (this.filteredStores.length === 0) {
            console.log('No stores to display, showing empty state');
            this.showEmpty();
            this.hidePagination();
            return;
        }

        this.hideEmpty();

        const totalPages = Math.ceil(this.filteredStores.length / this.storesPerPage);
        const startIndex = (this.currentPage - 1) * this.storesPerPage;
        const endIndex = Math.min(startIndex + this.storesPerPage, this.filteredStores.length);
        const storesToShow = this.filteredStores.slice(startIndex, endIndex);

        console.log(`Showing stores ${startIndex} to ${endIndex} (${storesToShow.length} stores) of ${this.filteredStores.length} total`);

        storesToShow.forEach((store, index) => {
            try {
                const card = this.createStoreCard(store);
                this.gridEl.appendChild(card);
            } catch (error) {
                console.error(`Error creating card for store ${index}:`, error, store);
            }
        });

        // Render pagination if needed
        if (totalPages > 1) {
            this.renderPagination(totalPages);
        } else {
            this.hidePagination();
        }
    }

    renderPagination(totalPages) {
        if (!this.paginationEl) return;

        this.paginationEl.classList.remove('hidden');
        this.paginationEl.innerHTML = '';

        const paginationContainer = document.createElement('div');
        paginationContainer.className = 'store-locator__pagination-container';

        // Navigation wrapper (Previous, Page Numbers, Next)
        const navWrapper = document.createElement('div');
        navWrapper.className = 'store-locator__pagination-nav';

        // Previous button
        const prevButton = document.createElement('button');
        prevButton.className = 'store-locator__pagination-button';
        prevButton.textContent = 'Previous';
        prevButton.disabled = this.currentPage === 1;
        prevButton.addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.renderStores();
                // Scroll to top of results
                this.gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
        navWrapper.appendChild(prevButton);

        // Page number buttons (show: 1, 3 around current, last)
        const pageNumbers = document.createElement('div');
        pageNumbers.className = 'store-locator__pagination-numbers';

        // Always show page 1
        if (totalPages > 1) {
            const firstBtn = this.createPageButton(1);
            pageNumbers.appendChild(firstBtn);
        }

        // Calculate 3 pages around current (1 before, current, 1 after)
        let startPage = Math.max(2, this.currentPage - 1);
        let endPage = Math.min(totalPages - 1, this.currentPage + 1);

        // Adjust if we're near the beginning
        if (this.currentPage <= 2) {
            startPage = 2;
            endPage = Math.min(4, totalPages - 1);
        }

        // Adjust if we're near the end
        if (this.currentPage >= totalPages - 1) {
            startPage = Math.max(2, totalPages - 3);
            endPage = totalPages - 1;
        }

        // Add ellipsis after page 1 if needed
        if (startPage > 2) {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'store-locator__pagination-ellipsis';
            ellipsis.textContent = '...';
            pageNumbers.appendChild(ellipsis);
        }

        // Add the 3 pages around current (only if they're not page 1 or last)
        if (startPage <= endPage) {
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.appendChild(this.createPageButton(i));
            }
        }

        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'store-locator__pagination-ellipsis';
            ellipsis.textContent = '...';
            pageNumbers.appendChild(ellipsis);
        }

        // Always show last page (if there's more than 1 page)
        if (totalPages > 1) {
            const lastBtn = this.createPageButton(totalPages);
            pageNumbers.appendChild(lastBtn);
        }

        navWrapper.appendChild(pageNumbers);

        // Next button
        const nextButton = document.createElement('button');
        nextButton.className = 'store-locator__pagination-button';
        nextButton.textContent = 'Next';
        nextButton.disabled = this.currentPage === totalPages;
        nextButton.addEventListener('click', () => {
            if (this.currentPage < totalPages) {
                this.currentPage++;
                this.renderStores();
                // Scroll to top of results
                this.gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
        navWrapper.appendChild(nextButton);

        paginationContainer.appendChild(navWrapper);

        // Page info (below navigation)
        const pageInfo = document.createElement('div');
        pageInfo.className = 'store-locator__pagination-info';
        const startIndex = (this.currentPage - 1) * this.storesPerPage + 1;
        const endIndex = Math.min(this.currentPage * this.storesPerPage, this.filteredStores.length);
        pageInfo.textContent = `Showing ${startIndex}-${endIndex} of ${this.filteredStores.length} stores`;
        paginationContainer.appendChild(pageInfo);

        this.paginationEl.appendChild(paginationContainer);
    }

    createPageButton(pageNum) {
        const button = document.createElement('button');
        button.className = 'store-locator__pagination-page';
        if (pageNum === this.currentPage) {
            button.classList.add('active');
        }
        button.textContent = pageNum;
        button.addEventListener('click', () => {
            this.currentPage = pageNum;
            this.renderStores();
            // Scroll to top of results
            this.gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return button;
    }

    hidePagination() {
        if (this.paginationEl) {
            this.paginationEl.classList.add('hidden');
        }
    }

    createStoreCard(store) {
        const card = document.createElement('div');
        card.className = 'grid__item';

        const cardInner = document.createElement('div');
        cardInner.className = 'store-locator__card';

        // Store name
        const name = document.createElement('h3');
        name.className = 'store-locator__card-name';
        name.textContent = store.name;
        cardInner.appendChild(name);

        // Address
        const address = document.createElement('div');
        address.className = 'store-locator__card-address';

        if (store.address) {
            const addressLine = document.createElement('p');
            addressLine.textContent = store.address;
            address.appendChild(addressLine);
        }

        if (store.address2) {
            const address2Line = document.createElement('p');
            address2Line.textContent = store.address2;
            address.appendChild(address2Line);
        }

        const cityStateZip = document.createElement('p');
        const cityStateParts = [store.city, store.state, store.zip].filter(Boolean);
        cityStateZip.textContent = cityStateParts.join(', ');
        address.appendChild(cityStateZip);

        if (store.country && store.country !== 'US') {
            const countryLine = document.createElement('p');
            countryLine.textContent = store.country;
            address.appendChild(countryLine);
        }

        cardInner.appendChild(address);

        // Details
        const details = document.createElement('div');
        details.className = 'store-locator__card-details';

        if (store.phone) {
            const phoneDetail = this.createDetail('phone', store.phone);
            details.appendChild(phoneDetail);
        }

        if (store.hours) {
            const formattedHours = this.formatHours(store.hours);
            const hoursDetail = this.createDetail('clock', formattedHours, true);
            details.appendChild(hoursDetail);
        }

        if (store.url) {
            const urlDetail = this.createDetail('link', store.url, false, store.url);
            details.appendChild(urlDetail);
        }

        if (store.email) {
            const emailDetail = this.createDetail('email', store.email, false, `mailto:${store.email}`);
            details.appendChild(emailDetail);
        }

        cardInner.appendChild(details);

        // Google Maps link if coordinates available
        if (store.lat && store.lng) {
            const mapsUrl = `https://www.google.com/maps?q=${store.lat},${store.lng}`;
            const mapsLink = document.createElement('a');
            mapsLink.href = mapsUrl;
            mapsLink.target = '_blank';
            mapsLink.rel = 'noopener noreferrer';
            mapsLink.className = 'store-locator__card-link';
            mapsLink.innerHTML = 'Get Directions <svg class="icon icon-arrow" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 10" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.293 5.5H1a.5.5 0 010-1h10.293L8.646 1.854a.5.5 0 01-.109-.546z" fill="currentColor"/></svg>';
            cardInner.appendChild(mapsLink);
        }

        card.appendChild(cardInner);
        return card;
    }

    formatHours(hoursString) {
        if (!hoursString) return '';

        // Split by periods and clean up
        const days = hoursString.split('.').map(day => day.trim()).filter(day => day);
        const formattedDays = [];

        const dayNames = {
            'monday': 'Monday',
            'tuesday': 'Tuesday',
            'wednesday': 'Wednesday',
            'thursday': 'Thursday',
            'friday': 'Friday',
            'saturday': 'Saturday',
            'sunday': 'Sunday'
        };

        days.forEach(day => {
            // Find the colon to separate day name from hours
            const colonIndex = day.indexOf(':');
            if (colonIndex === -1) return;

            const dayName = day.substring(0, colonIndex).trim().toLowerCase();
            const hours = day.substring(colonIndex + 1).trim();

            // Capitalize day name
            const capitalizedDay = dayNames[dayName] || dayName.charAt(0).toUpperCase() + dayName.slice(1);

            // Format hours (add space before AM/PM if needed, capitalize closed)
            let formattedHours = hours;
            if (hours.toLowerCase() === 'closed') {
                formattedHours = 'Closed';
            } else {
                // Ensure space before AM/PM
                formattedHours = formattedHours.replace(/(\d)(AM|PM)/gi, '$1 $2');
            }

            formattedDays.push(`${capitalizedDay}: ${formattedHours}`);
        });

        return formattedDays.join('\n');
    }

    createDetail(iconType, content, isHours = false, link = null) {
        const detail = document.createElement('div');
        detail.className = 'store-locator__card-detail';

        const icon = document.createElement('div');
        icon.className = 'store-locator__card-detail-icon';
        icon.innerHTML = this.getIconSVG(iconType);
        detail.appendChild(icon);

        const contentEl = document.createElement('div');
        contentEl.className = `store-locator__card-detail-content${isHours ? ' store-locator__card-hours' : ''}`;

        if (link) {
            const linkEl = document.createElement('a');
            linkEl.href = link;
            linkEl.target = link.startsWith('http') ? '_blank' : '_self';
            linkEl.rel = link.startsWith('http') ? 'noopener noreferrer' : '';
            linkEl.textContent = content;
            linkEl.style.color = 'inherit';
            linkEl.style.textDecoration = 'none';
            contentEl.appendChild(linkEl);
        } else if (isHours) {
            // For hours, preserve line breaks and format nicely
            const lines = content.split('\n');
            lines.forEach((line, index) => {
                if (line.trim()) {
                    const lineEl = document.createElement('div');
                    lineEl.className = 'store-locator__hours-line';
                    lineEl.textContent = line.trim();
                    contentEl.appendChild(lineEl);
                }
            });
        } else {
            contentEl.textContent = content;
        }

        detail.appendChild(contentEl);
        return detail;
    }

    getIconSVG(iconType) {
        const icons = {
            phone: '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/></svg>',
            clock: '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>',
            link: '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-7z"/><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/></svg>',
            email: '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>'
        };

        return icons[iconType] || icons.link;
    }

    showLoading() {
        if (this.loadingEl) {
            this.loadingEl.classList.remove('hidden');
        }
        if (this.gridEl) {
            this.gridEl.style.opacity = '0.5';
        }
    }

    hideLoading() {
        if (this.loadingEl) {
            this.loadingEl.classList.add('hidden');
        }
        if (this.gridEl) {
            this.gridEl.style.opacity = '1';
        }
    }

    showEmpty() {
        if (this.emptyEl) {
            this.emptyEl.classList.remove('hidden');
        }
    }

    hideEmpty() {
        if (this.emptyEl) {
            this.emptyEl.classList.add('hidden');
        }
    }
}

// Make it globally available
window.StoreLocator = StoreLocator;

// Auto-initialize if DOM is already ready
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    // Script loaded after DOM, but we'll let the inline script handle initialization
    console.log('StoreLocator script loaded');
}

